import { getCustomizations } from '../components/configurable/customercare';
import { TFunction } from '../hooks/useTranslation.hook';
import * as AppEnvironment from '../appSettings/app.environment';
import * as AppSettingsHelpers from '../appSettings/app.settings.helpers';
import {
  FeatureToggleDataType,
  FeatureToggleType,
  UICustomizationFeatureToggle,
  UIFeatureToggle
} from './featureToggle.constants';
import {
  FeatureCategory,
  FeatureToggleCatalogGroup,
  FeatureToggleCatalogItem,
  getUIFeatureToggleConfigFields
} from './featureToggle.catalog';

export type OverridableFeatureToggleKey = string;

export interface OverridableFeatureToggle {
  key: OverridableFeatureToggleKey;
  label: string;
  info?: string;
  category: FeatureCategory;
}

export interface OverridableFeatureToggleGroup {
  title: string;
  items: OverridableFeatureToggle[];
}

export enum OverrideValue {
  Default = 'default',
  ForceOn = 'force-on',
  ForceOff = 'force-off'
}

const UI_FEATURE_TOGGLE_KEYS = new Set<string>(Object.values(UIFeatureToggle));
const UI_CUSTOMIZATION_TOGGLE_KEYS = new Set<string>(
  Object.values(UICustomizationFeatureToggle)
);

function isOverridableKey(key: string): boolean {
  return (
    UI_FEATURE_TOGGLE_KEYS.has(key) || UI_CUSTOMIZATION_TOGGLE_KEYS.has(key)
  );
}

/**
 * Whether per-user Feature Overrides should apply (and whether the React
 * panel should be offered). Local, edge, dbt, and QA hosts; never production.
 */
export function areFeatureOverridesEnabled(): boolean {
  return (
    AppEnvironment.isDevelopmentEnvironment() ||
    AppSettingsHelpers.isQAEnvironment()
  );
}

/**
 * Whether the Feature Overrides panel should be available. Same host rules as
 * applying saved overrides.
 */
export function isFeatureOverridesPanelAvailable(): boolean {
  return areFeatureOverridesEnabled();
}

/**
 * Builds the grouped, human-labeled catalog of toggles eligible for per-user
 * override. Uses Configuration UI Feature Toggle metadata plus boolean
 * Customer Care customizations. Pending-removal / disabled entries are omitted.
 */
export function getOverridableFeatureToggleGroups(
  t: TFunction
): OverridableFeatureToggleGroup[] {
  const seen = new Set<string>();
  const groups: OverridableFeatureToggleGroup[] = [];

  for (const group of getUIFeatureToggleConfigFields(t)) {
    const items: OverridableFeatureToggle[] = [];
    for (const item of group.items) {
      if (
        item.disabled ||
        !isOverridableKey(item.name) ||
        seen.has(item.name)
      ) {
        continue;
      }
      seen.add(item.name);
      items.push({
        key: item.name,
        label: item.label,
        info: item.info,
        category: item.category
      });
    }
    if (items.length) {
      groups.push({ title: group.title, items });
    }
  }

  const customizationLabels = getCustomizations(t);
  const customizationItems: OverridableFeatureToggle[] = [];
  for (const key of Object.keys(customizationLabels)) {
    if (!isOverridableKey(key) || seen.has(key)) {
      continue;
    }
    seen.add(key);
    customizationItems.push({
      key,
      label: customizationLabels[key as keyof typeof customizationLabels],
      category: FeatureCategory.Customizations
    });
  }
  if (customizationItems.length) {
    groups.push({
      title: t('Customizations'),
      items: customizationItems
    });
  }

  return groups;
}

let cachedEligibleKeys: Set<OverridableFeatureToggleKey> | undefined;

/**
 * Keys eligible for per-user override. Labels are irrelevant here, so the
 * catalog is walked once with an identity translator and cached — this runs on
 * every header render and toggle merge, and rebuilding the catalog each time is
 * expensive.
 */
export function getOverridableFeatureToggleKeys(): Set<OverridableFeatureToggleKey> {
  if (!cachedEligibleKeys) {
    const keys = new Set<OverridableFeatureToggleKey>();
    const identity = ((key: string) => key) as TFunction;
    for (const group of getOverridableFeatureToggleGroups(identity)) {
      for (const item of group.items) {
        keys.add(item.key);
      }
    }
    cachedEligibleKeys = keys;
  }
  return cachedEligibleKeys;
}

/**
 * Keeps only eligible overridable keys from a persisted preference blob.
 * Drops API toggles, custom values, retired/disabled keys, and unknown keys.
 */
export function filterEligibleFeatureToggleOverrides(
  overrides: Partial<Record<FeatureToggleDataType, boolean>> | null | undefined,
  eligibleKeys?: Set<string>
): Partial<Record<string, boolean>> {
  if (!overrides) {
    return {};
  }
  const allowed = eligibleKeys ?? getOverridableFeatureToggleKeys();

  const result: Partial<Record<string, boolean>> = {};
  for (const [key, value] of Object.entries(overrides)) {
    if (
      allowed.has(key) &&
      typeof value === 'boolean' &&
      isOverridableKey(key)
    ) {
      result[key] = value;
    }
  }
  return result;
}

/** Maps a stored override to the tri-state value shown in the override panel. */
export function toOverrideValue(
  overrides: Partial<Record<FeatureToggleDataType, boolean>> | null | undefined,
  key: OverridableFeatureToggleKey
): OverrideValue {
  if (!overrides) {
    return OverrideValue.Default;
  }
  const overrideMap = overrides as Record<string, boolean | undefined>;
  if (!(key in overrideMap)) {
    return OverrideValue.Default;
  }
  return overrideMap[key] ? OverrideValue.ForceOn : OverrideValue.ForceOff;
}

/**
 * Builds the preference JSON object from the current form only.
 * Default values are omitted. Historical keys not present in the form are dropped.
 */
export function toPersistedOverrides(
  formValues: Record<string, OverrideValue>
): Partial<Record<string, boolean>> {
  const result: Partial<Record<string, boolean>> = {};

  for (const [key, value] of Object.entries(formValues)) {
    if (!isOverridableKey(key)) {
      continue;
    }
    if (value === OverrideValue.ForceOn) {
      result[key] = true;
    } else if (value === OverrideValue.ForceOff) {
      result[key] = false;
    }
  }

  return result;
}

export function getActiveOverrideCount(
  overrides: Partial<Record<FeatureToggleDataType, boolean>> | null | undefined
): number {
  return Object.keys(filterEligibleFeatureToggleOverrides(overrides)).length;
}

/**
 * Filters a FeatureToggle JSON map to keys defined in FeatureToggleType.
 * Used by Configuration Template Configuration save (preserves prior behavior
 * of keeping any known FeatureToggleType key, including API toggles if present).
 */
export function filterFeatureTogglesByCatalog(
  featureToggleValues: Partial<Record<string, boolean>>
): Partial<Record<FeatureToggleDataType, boolean>> {
  const codebaseKeys = new Set<string>(
    Object.values(FeatureToggleType) as string[]
  );

  return Object.fromEntries(
    Object.entries(featureToggleValues).filter(([key]) => codebaseKeys.has(key))
  ) as Partial<Record<FeatureToggleDataType, boolean>>;
}

/**
 * Filters Customer Care Template customizations[] to known customization keys.
 */
export function filterCustomizationsByCatalog(
  customizations: string[] | null | undefined
): string[] {
  if (!customizations?.length) {
    return [];
  }
  return customizations.filter((key) => UI_CUSTOMIZATION_TOGGLE_KEYS.has(key));
}

/** Flatten catalog groups for tests / callers that need a flat list. */
export function flattenCatalogItems(
  groups: FeatureToggleCatalogGroup[]
): FeatureToggleCatalogItem[] {
  return groups.flatMap((group) => group.items);
}
